import { db } from '@/config/firebase';
import { getVisibleDeviceDocs } from '@/lib/visibleDevices';
import { collection, doc, query, where, getDoc, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { Flow, Execution, Modem } from '@/types/firestore';
import { retryOperation } from '@/utils/error-handler';
import { isInternalExecution } from '@simcheck/shared-types';
import { toDate } from '@/lib/date-utils';

/**
 * Dashboard Metrics
 */
export interface DashboardMetrics {
  totalFlows: number;
  activeFlows: number;
  totalModems: number;
  activeModems: number;
  todayExecutions: number;
  successRate: number;
  avgDuration: number;
}

/**
 * Execution Trend Data Point
 */
export interface ExecutionTrendData {
  timestamp: Date;
  successful: number;
  failed: number;
  total: number;
}

/**
 * Performance Breakdown
 */
export interface PerformanceBreakdown {
  successful: number;
  failed: number;
  pending: number;
}

/**
 * Modem Activity Data Point
 */
export interface ModemActivityData {
  timestamp: Date;
  active: number;
  idle: number;
}

/**
 * Recent Activity Item
 */
export interface RecentActivityItem {
  id: string;
  flowName: string;
  modemName: string;
  status: 'completed' | 'failed' | 'running';
  duration: number;
  timestamp: Date;
}

/**
 * Modem tallies across every device the org can test on: its own private edges
 * (`ownerOrgId === orgId`) plus the shared commons (`ownerOrgId == null`) — the
 * same visibility rule the dashboard map and Global Locations panel apply.
 *
 * Reads `devices`, NOT `edge_devices`: central-server writes heartbeats to
 * `devices` keyed by `ownerOrgId`, so the old `edge_devices` + `organizationId`
 * query matched nothing and every tenant saw "0 of 0 total".
 *
 * A modem counts as online when its DEVICE is online — real device docs carry
 * `modems[].status === null` (the per-modem state lives in the `known_modems`
 * subcollection), so filtering on that field zeroes the count too.
 */
async function getVisibleModemCounts(
  organizationId: string
): Promise<{ totalModems: number; activeModems: number; idleModems: number }> {
  // Shared commons + the org's own edges, as two rule-provable queries.
  const deviceDocs = await getVisibleDeviceDocs(db, organizationId, false);

  let totalModems = 0;
  let activeModems = 0;
  let idleModems = 0;

  deviceDocs.forEach((doc) => {
    const data = doc.data() as any;
    const ownerOrgId = data.ownerOrgId ?? null;
    if (ownerOrgId !== null && ownerOrgId !== organizationId) return;

    const count = Array.isArray(data.modems)
      ? data.modems.length
      : (data.health?.modemCount ?? 0);
    totalModems += count;
    if (data.status === 'online') {
      activeModems += count;
    } else {
      idleModems += count;
    }
  });

  return { totalModems, activeModems, idleModems };
}

/**
 * Get Dashboard Metrics
 */
export async function getDashboardMetrics(organizationId: string): Promise<DashboardMetrics> {
  return retryOperation(async () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Get total flows
    const flowsQuery = query(
      collection(db, 'flows'),
      where('organizationId', '==', organizationId)
    );
    const flowsSnapshot = await getDocs(flowsQuery);
    const totalFlows = flowsSnapshot.size;
    const activeFlows = flowsSnapshot.docs.filter((doc) => {
      const data = doc.data() as Flow;
      return data.status === 'draft' || data.status === 'active';
    }).length;

    // Modems across every device the org can test on (own + shared)
    const { totalModems, activeModems } = await getVisibleModemCounts(organizationId);

    // Get today's executions
    const executionsQuery = query(
      collection(db, 'executions'),
      where('organizationId', '==', organizationId),
      where('startedAt', '>=', Timestamp.fromDate(startOfToday))
    );
    const executionsSnapshot = await getDocs(executionsQuery);
    // Platform-operated runs (device-setup clicks, not customer tests) never
    // count toward a tenant's volume or success rate — see #267.
    const executions = executionsSnapshot.docs
      .map((doc) => doc.data() as Execution)
      .filter((e) => !isInternalExecution(e));
    const todayExecutions = executions.length;

    // Calculate success rate and avg duration
    const completedExecutions = executions.filter(
      (e) => e.status === 'completed' || e.status === 'failed'
    );
    const successfulExecutions = executions.filter((e) => e.status === 'completed');
    const successRate =
      completedExecutions.length > 0
        ? (successfulExecutions.length / completedExecutions.length) * 100
        : 0;

    // Calculate average duration (in seconds)
    const durationsInMs = completedExecutions
      .filter((e) => e.completedAt && e.startedAt)
      .map((e) => {
        const start = (toDate(e.startedAt) || new Date()).getTime();
        const end = (toDate(e.completedAt) || new Date()).getTime();
        return end - start;
      });
    const avgDuration =
      durationsInMs.length > 0
        ? Math.round(durationsInMs.reduce((sum, d) => sum + d, 0) / durationsInMs.length / 1000)
        : 0;

    return {
      totalFlows,
      activeFlows,
      totalModems,
      activeModems,
      todayExecutions,
      successRate: parseFloat(successRate.toFixed(1)),
      avgDuration,
    };
  });
}

/**
 * Determine bucket size based on time range
 * - 1-12 hours: 15-minute buckets
 * - 13-48 hours: 1-hour buckets
 * - 49+ hours: 4-hour buckets
 */
function getBucketConfig(hours: number): { bucketMinutes: number; maxBuckets: number } {
  if (hours <= 12) {
    return { bucketMinutes: 15, maxBuckets: Math.ceil(hours * 4) };
  } else if (hours <= 48) {
    return { bucketMinutes: 60, maxBuckets: Math.ceil(hours) };
  } else {
    return { bucketMinutes: 240, maxBuckets: Math.ceil(hours / 4) };
  }
}

/**
 * Get bucket key for a timestamp based on bucket size
 */
function getBucketKey(timestamp: Date, bucketMinutes: number): string {
  const bucketStart = new Date(timestamp);
  const minutes = bucketStart.getMinutes();
  const roundedMinutes = Math.floor(minutes / bucketMinutes) * bucketMinutes;
  bucketStart.setMinutes(roundedMinutes, 0, 0);

  if (bucketMinutes >= 60) {
    const hours = bucketStart.getHours();
    const roundedHours = Math.floor(hours / (bucketMinutes / 60)) * (bucketMinutes / 60);
    bucketStart.setHours(roundedHours, 0, 0, 0);
  }

  return bucketStart.toISOString();
}

/**
 * Get Execution Trends (last N hours) with dynamic bucketing
 */
export async function getExecutionTrends(
  organizationId: string,
  hours: number = 24
): Promise<ExecutionTrendData[]> {
  return retryOperation(async () => {
    const now = new Date();
    const startTime = new Date(now.getTime() - hours * 60 * 60 * 1000);
    const { bucketMinutes, maxBuckets } = getBucketConfig(hours);

    const executionsQuery = query(
      collection(db, 'executions'),
      where('organizationId', '==', organizationId),
      where('startedAt', '>=', Timestamp.fromDate(startTime)),
      orderBy('startedAt', 'asc')
    );
    const executionsSnapshot = await getDocs(executionsQuery);
    const executions = executionsSnapshot.docs
      .map((doc) => doc.data() as Execution)
      .filter((e) => !isInternalExecution(e));

    // Group by dynamic bucket
    const bucketData = new Map<string, { successful: number; failed: number; total: number }>();
    for (const execution of executions) {
      if (!execution.startedAt) continue;

      const timestamp = toDate(execution.startedAt) || new Date();
      const bucketKey = getBucketKey(timestamp, bucketMinutes);

      if (!bucketData.has(bucketKey)) {
        bucketData.set(bucketKey, { successful: 0, failed: 0, total: 0 });
      }

      const data = bucketData.get(bucketKey)!;
      data.total += 1;
      if (execution.status === 'completed') {
        data.successful += 1;
      } else if (execution.status === 'failed') {
        data.failed += 1;
      }
    }

    // Generate all bucket timestamps and fill with data
    const result: ExecutionTrendData[] = [];
    const bucketMs = bucketMinutes * 60 * 1000;

    for (let i = maxBuckets; i >= 0; i--) {
      const bucketTime = new Date(now.getTime() - i * bucketMs);
      const bucketKey = getBucketKey(bucketTime, bucketMinutes);
      const data = bucketData.get(bucketKey) || { successful: 0, failed: 0, total: 0 };
      result.push({
        timestamp: new Date(bucketKey),
        ...data,
      });
    }

    return result;
  });
}

/**
 * Get Performance Breakdown
 */
export async function getPerformanceBreakdown(organizationId: string): Promise<PerformanceBreakdown> {
  return retryOperation(async () => {
    const executionsQuery = query(
      collection(db, 'executions'),
      where('organizationId', '==', organizationId)
    );
    const executionsSnapshot = await getDocs(executionsQuery);
    const executions = executionsSnapshot.docs
      .map((doc) => doc.data() as Execution)
      .filter((e) => !isInternalExecution(e));

    const successful = executions.filter((e) => e.status === 'completed').length;
    const failed = executions.filter((e) => e.status === 'failed').length;
    const pending = executions.filter((e) => e.status === 'running' || e.status === 'pending').length;

    return { successful, failed, pending };
  });
}

/**
 * Get Modem Activity (last N hours)
 */
export async function getModemActivity(
  organizationId: string,
  hours: number = 24
): Promise<ModemActivityData[]> {
  return retryOperation(async () => {
    // Modems across every device the org can test on (own + shared)
    const { activeModems: activeCount, idleModems: idleCount } =
      await getVisibleModemCounts(organizationId);

    // Generate hourly data (in a real app, this would come from historical data)
    const now = new Date();
    const result: ModemActivityData[] = [];
    for (let i = hours; i >= 0; i--) {
      const timestamp = new Date(now.getTime() - i * 60 * 60 * 1000);
      result.push({
        timestamp,
        active: activeCount,
        idle: idleCount,
      });
    }

    return result;
  });
}

/**
 * Get Recent Activity
 */
export async function getRecentActivity(
  organizationId: string,
  limitCount: number = 5
): Promise<RecentActivityItem[]> {
  return retryOperation(async () => {
    const executionsQuery = query(
      collection(db, 'executions'),
      where('organizationId', '==', organizationId),
      orderBy('startedAt', 'desc'),
      // Over-fetch: internal runs are dropped below, and Firestore can't
      // express "not one of these sources" alongside an orderBy.
      limit(limitCount * 2)
    );
    const executionsSnapshot = await getDocs(executionsQuery);
    const executions = (executionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as (Execution & { id: string })[])
      .filter((e) => !isInternalExecution(e))
      .slice(0, limitCount);

    // Fetch related flow and modem names.
    // getDoc (get-by-id), NOT a `where __name__ ==` LIST query: the flows LIST
    // rule excludes system flows and evaluates against `resource.data` — so a
    // ref to a system flow, another org's flow, or a since-deleted flow (the
    // demo seed leaves executions pointing at flows that don't exist) throws
    // permission-denied. The get-by-`read` rule allows system flows, and a
    // missing doc reads as "not found" instead of 403. Each lookup is also
    // wrapped so one unreadable ref degrades to a fallback label rather than
    // taking down the whole dashboard (issue: tenant saw the permission banner
    // + all-zero metrics whenever a demo-seed execution was among the newest).
    const result: RecentActivityItem[] = [];
    for (const execution of executions) {
      // Get flow name
      let flowName = 'Unknown Flow';
      if (execution.flowId) {
        try {
          const flowSnapshot = await getDoc(doc(db, 'flows', execution.flowId));
          if (flowSnapshot.exists()) {
            flowName = (flowSnapshot.data() as Flow).name;
          }
        } catch {
          // unreadable ref (system/other-org/deleted flow) — keep fallback
        }
      }

      // Get modem name
      let modemName = 'Unknown Modem';
      if (execution.modemId) {
        try {
          const modemSnapshot = await getDoc(doc(db, 'modems', execution.modemId));
          if (modemSnapshot.exists()) {
            modemName = (modemSnapshot.data() as Modem).name;
          }
        } catch {
          // unreadable ref — keep fallback
        }
      }

      // Calculate duration
      let duration = 0;
      if (execution.completedAt && execution.startedAt) {
        const start = (toDate(execution.startedAt) || new Date()).getTime();
        const end = (toDate(execution.completedAt) || new Date()).getTime();
        duration = Math.round((end - start) / 1000);
      }

      result.push({
        id: execution.id,
        flowName,
        modemName,
        status: execution.status as 'completed' | 'failed' | 'running',
        duration,
        timestamp: execution.startedAt ? toDate(execution.startedAt) || new Date() : new Date(),
      });
    }

    return result;
  });
}

